import React, { useEffect, useState } from "react";
import { Text } from "react-native";
import { Image, Pressable, View } from "react-native";
import * as Google from "expo-auth-session/providers/google";
import * as AuthSession from "expo-auth-session";
import { notificationService } from "../services/notification-service";

interface GoogleAuthButtonMobileProps {
  navigation: any;
  RBRef: React.RefObject<any>;
  destination: string;
  onSubmit: (email: string, password: string, logintype: string) => void;
}

/**
 * Component to render a Google button for authentication.
 *
 * Uses the `expo-auth-session` library to perform the Google OAuth flow.
 * The component renders a button with the Google logo and the text "Continue with Google".
 * When the button is pressed, it starts the Google OAuth flow.
 * If the user is authenticated, it calls the onSubmit function with the user's email, ID, and a boolean indicating that
 * the user logged in with Google.
 * If the user is not authenticated, it sends a notification with an error message and closes the RBSheet.
 * @param navigation - Navigation object to manage app navigation.
 * @param RBRef - Ref to the BottomSheetModal component to dismiss when the Google OAuth flow is complete.
 * @param destination - The destination route name for navigation.
 * @param onSubmit - Function to call with the user's email, ID, and a boolean indicating that the user logged in with Google.
 * @returns A Pressable component with the Google logo and the text "Continue with Google".
 */

const GoogleAuthButtonMobile: React.FC<GoogleAuthButtonMobileProps> = ({
  navigation,
  RBRef,
  destination,
  onSubmit,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [_request, response, promptAsync] = Google.useAuthRequest({
    iosClientId: process.env.REACT_NATIVE_GOOGLE_IOS_CLIENT_ID!,
    androidClientId: process.env.REACT_NATIVE_GOOGLE_ANDROID_CLIENT_ID!,
    responseType: AuthSession.ResponseType.Code,
    scopes: ["email", "profile"],
  });

  useEffect(() => {
    handleSignInWithGoogle();
  }, [response]);

  const handleSignInWithGoogle = async () => {
    if (response?.type === "success") {
      await getUserInfo(response.authentication?.accessToken);
      RBRef.current?.dismiss();
    }
  };

  const getUserInfo = async (token: string | undefined) => {
    setIsLoading(true);

    try {
      const response = await fetch(
        "https://www.googleapis.com/userinfo/v2/me",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const user = await response.json();      
      // TODO: We need to get the actual OAuth sub from the ID token
      onSubmit(user?.email, user?.id, 'google');
    } catch (e) {
      notificationService.sendNotification(
        "error",
        "Unable to sign in using Google currently. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Pressable
      disabled={isLoading}
      onPress={() => promptAsync()}
      className="rounded-full"
    >
      <View className="flex-row justify-center items-center px-4 py-3 my-1 bg-white rounded-full">
        <Image
          source={require("../../assets/googleIcon.png")}
          style={{
            width: 24,
            height: 24,
          }}
          resizeMode="contain" // Ensures the image scales properly without being cropped
        />
        <Text className="ml-4 text-lg font-medium text-black">
          Continue with Google
        </Text>
      </View>
    </Pressable>
  );
};

export default GoogleAuthButtonMobile;
